/**
 * useAutoBpm — a small auto-BPM flow that drives the shared <AutoBpmDialog>.
 *
 * For editors with a simple detector (Synth VR, Console): it shows the metronome
 * popup while detecting, applies the result, then lands on "done" (where the
 * user can fine-tune to a round value). Flow keeps its own richer flow (online
 * verification + octave reconciliation) and drives the same dialog directly.
 */

import { useState, useCallback } from 'react';
import type { BpmStage } from './AutoBpmDialog';

export function useAutoBpm(detect: () => Promise<number>, apply: (bpm: number) => void) {
  const [stage, setStage] = useState<BpmStage>(null);
  const [detected, setDetected] = useState<number | null>(null);

  const choose = useCallback((bpm: number) => {
    const v = Math.round(bpm * 100) / 100;
    apply(v);
    setDetected(v);
    setStage('done');
  }, [apply]);

  const run = useCallback(async () => {
    if (stage === 'detecting') return;
    setStage('detecting');
    try {
      const bpm = await detect();
      const v = Math.round(bpm * 100) / 100;
      apply(v);
      setDetected(v);
      setStage('done');
    } catch {
      setStage(null);
    }
  }, [stage, detect, apply]);

  const dismiss = useCallback(() => setStage(null), []);

  return { stage, detected, run, choose, dismiss, busy: stage === 'detecting' };
}
